import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnResizeBar from "@shared/components/SnResizeBar";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import { menuInfo } from "@shared/type/CommonType";
import { dateTimeToString, dateToString } from "@shared/utils/DateUtils";
import dayjs from "dayjs";

interface Props {
  Menuprops: menuInfo;
}

// 2단그리드
const MENU_ID = "SN_PRD_TOO_REG_003"; // 메뉴코드
const SEARCH_QUERY_ID = "sn_prd_too_reg_003_01"; // 조회쿼리
const SAVE_ENDPOINT = "SN_PRD_TOO_REG_003_SAVE_01"; // 저장API
const LINK_COLUMNS = ["no_tool", "no_sn"]; // 메뉴 메인컬럼 ex) 수주등록 no_so, 발주등록 no_po

const LayoutG2 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region variable
  const { userInfo } = global();
  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
  });
  //#endregion

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData({
          menuInfo: Menuprops || ({ cd_menu: MENU_ID } as menuInfo),
          etcInfo: {},
        });

        setFormControlData(rtn.formControlData);
        setFormData(rtn.formData);
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
        });
      } catch (error: any) {}
    };

    fetchform();
  }, [Menuprops]);

  //#region Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
    onSave,
    onInsert,
    onDelete,
  }));

  const hasChanges = () => {
    const grid1change = getGridCurrent("grid1").getChange();
    const grid2change = getGridCurrent("grid2").getChange();

    return grid1change.modifiedRowcount > 0 || grid2change.modifiedRowcount > 0;
  };

  const onSearch = async () => {
    try {
      if (hasChanges()) {
        const confirm = await showDialog(
          "confirm",
          "수정된 내용이 있습니다. 저장하시겠습니까?"
        );

        if (confirm) {
          const isSaved = await onSave();
          if (!isSaved) return;
        }
      }

      const params = {
        id: SEARCH_QUERY_ID,
        params: searchRef.current.getFormdata(),
      };

      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();

      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0] || [],
        grid2: data[1] || [],
      });
    } catch (error: any) {}
  };

  const onSave = async (msg?: string) => {
    try {
      getGridCurrent("grid2").stopEditing();

      const requiredValuesCheckL =
        getGridCurrent("grid2").requiredValuesCheck();
      if (!requiredValuesCheckL) return false;

      const grid2change = getGridCurrent("grid2").getChange();

      if (grid2change.modifiedRowcount === 0) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      // grid2change.insert?.forEach((row: any) => {
      //   if (row.tm_use) {
      //     row.tm_use = dayjs(row.tm_use).toISOString();
      //   }
      // });

      // grid2change.update?.forEach((row: any) => {
      //   if (row.tm_use) {
      //     row.tm_use = dayjs(row.tm_use).toISOString();
      //   }
      // });
      // grid2change.insert?.forEach((row: any) => {
      //   if (row.tm_use) {
      //     row.tm_use = dayjs(row.tm_use).format("YYYY-MM-DD HH:mm:ss");
      //   }
      // });

      // grid2change.update?.forEach((row: any) => {
      //   if (row.tm_use) {
      //     row.tm_use = dayjs(row.tm_use).format("YYYY-MM-DD HH:mm:ss");
      //   }
      // });

      const saveData = {
        tool_use: grid2change,
      };

      const ok = await fnSave(SAVE_ENDPOINT, saveData);
      if (!ok) return false;

      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      return true;
    } catch (error: any) {
      getGridCurrent("grid1").resetUpdate(true);
      getGridCurrent("grid2").resetUpdate(true);
      return false;
    }
  };

  const onInsert = async () => {
    try {
      return;
    } catch (error: any) {}
  };

  const onDelete = async () => {
    try {
      return;
    } catch (error: any) {}
  };

  const onAddRow = () => {
    try {
      getGridCurrent("grid1").stopEditing();
      getGridCurrent("grid2").stopEditing();
      return getGridCurrent("grid2").addRow();
    } catch (error: any) {}
  };

  const onDeleteRow = async () => {
    try {
      if (getGridCurrent("grid2").getDisplayedRowCount() < 1) return;
      getGridCurrent("grid2").deleteSelectedRows();
    } catch (error: any) {}
  };

  const onGrid2BtnsClick = async (id: string) => {
    if (id === "addRow") onAddRow();
    if (id === "deleteRow") onDeleteRow();
  };
  //#endregion

  //#region Grid Event
  const onBeforeAddRowH = (): any => {
    return {
      cd_firm: userInfo.cd_firm,
    };
  };

  const onBeforeAddRowL = (): any => {
    const focusedNodeH = getGridCurrent("grid1").focusedRowNode();
    if (!focusedNodeH) {
      showDialog("alter", "선택된 행이 없습니다.");
      return false;
    }

    const requiredValuesCheckH = getGridCurrent("grid1").requiredValuesCheck();
    if (!requiredValuesCheckH) return false;

    const maxNoLine = gridRefs.current["grid2"].current.getMaxValue("no_line");
    return {
      cd_firm: userInfo.cd_firm,
      no_tool: focusedNodeH.data?.no_tool,
      no_sn: focusedNodeH.data?.no_sn,
      no_wo: "",
      dt_use: dateToString(new Date()),
      tm_use: dateTimeToString(new Date()),
      yn_insp: "Y",
      no_line: maxNoLine + 1,
    };
  };

  const onFocusedRowChangedH = (event: any) => {
    const selectedRow = event?.data || event;
    if (!selectedRow) return;

    const filterConditions = LINK_COLUMNS.reduce((acc: any, column) => {
      acc[column] = selectedRow[column];
      return acc;
    }, {});

    getGridCurrent("grid2").applyFilters(filterConditions);
    getGridCurrent("grid2").selectAndFocusRow(0);
  };
  //#endregion

  //#region useMemo
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onBeforeAddRow: onBeforeAddRowH,
          onFocusedRowChanged: onFocusedRowChangedH,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onBeforeAddRow: onBeforeAddRowL,
          onBtnAddClick: onGrid2BtnsClick,
          onBtnDeleteClick: onGrid2BtnsClick,
        },
      },
    }),
    [columns, dataSources]
  );
  //#endregion

  //#region UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{
              rowSelection: "single",
              title: "사용이력",
            }}
            events={grids.grid1.events}
            filter={{
              refs: [gridRefs.current["grid2"]],
              columns: LINK_COLUMNS,
            }}
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid2"]}
            columnConfig={grids.grid2.columns}
            rowData={grids.grid2.dataSource}
            etcProps={{
              rowSelection: "single",
              title: "상세정보",
            }}
            events={grids.grid2.events}
          />
        </SnResizeBar>
      </div>
    </div>
  );
  //#endregion
});

export default LayoutG2;
